/*
 * Copyright (c) 2023 Roboto Technologies, Inc.
 */
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Paper,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import relativeTime from "dayjs/plugin/relativeTime";
import * as React from "react";

import { useAuth } from "@/providers";
import { RobotoTypography } from "@/shared/components";
import {
  FARGATE_RAM_GB_USD_PER_HOUR,
  FARGATE_VCPU_USD_PER_HOUR,
} from "@/shared/domain/actions/computeCredits";
import {
  INVOCATIONS_SCHEDULED_RESOURCES,
  INVOCATION_RAM_GB_MILLIS_RESOURCES,
  INVOCATION_RUNTIME_MILLIS_RESOURCES,
  INVOCATION_VCPU_MILLIS_RESOURCES,
  MeteringReport,
  MeteredResourceValue,
  PROVIDER_COST_MICRODOLLARS_RESOURCES,
  sumMeteredResources,
} from "@/shared/domain/metering";
import { Resource } from "@/types/resources";

import { agentUnitCostUsd, USD_PER_AGENT_UNIT } from "./aiCreditFormatter";

dayjs.extend(duration);
dayjs.extend(relativeTime);

function humanFileSize(size: number) {
  const i = size === 0 ? 0 : Math.floor(Math.log(size) / Math.log(1024));
  return (
    (size / Math.pow(1024, i)).toFixed(2) +
    " " +
    ["B", "kB", "MB", "GB", "TB"][i]
  );
}

function humanDuration(millis: number) {
  return (millis / 60000).toFixed(2) + " minutes";
}

// The vCPU and RAM meters store CPU units (1024 per vCPU) and MiB, each times
// seconds, so both convert to whole units times minutes the same way.
function perUnitMinutes(unitSeconds: number) {
  return (unitSeconds / (1024 * 60)).toFixed(2);
}

export interface SystemUsageProps {
  latestReport: MeteringReport | null;
  /** Agent units used this month, or `undefined` while unknown. */
  agentUnitMillionths: number | undefined;
}

export const SystemUsage: React.FC<SystemUsageProps> = ({
  latestReport,
  agentUnitMillionths,
}) => {
  const { currentUser } = useAuth();
  const theme = useTheme();

  const valuesByName: { [key: string]: MeteredResourceValue } = {};

  const lastUpdated = React.useMemo(() => {
    if (!latestReport?.end_time) {
      return "Never";
    }

    return dayjs
      .duration(dayjs(latestReport.end_time).diff(dayjs()))
      .humanize(true);
  }, [latestReport]);

  if (latestReport) {
    latestReport.values.forEach((value) => {
      valuesByName[value.resource_name] = value;
    });
  }

  const datasets = valuesByName[Resource.DatasetsCreated]?.quantity || 0;
  const invocations = sumMeteredResources(
    latestReport,
    INVOCATIONS_SCHEDULED_RESOURCES,
  );
  const invocationVCpuMillis = sumMeteredResources(
    latestReport,
    INVOCATION_VCPU_MILLIS_RESOURCES,
  );
  const invocationRamMillis = sumMeteredResources(
    latestReport,
    INVOCATION_RAM_GB_MILLIS_RESOURCES,
  );
  const invocationRuntimeMillis = sumMeteredResources(
    latestReport,
    INVOCATION_RUNTIME_MILLIS_RESOURCES,
  );
  const externalDownloadBytes =
    valuesByName[Resource.ManagedDataDownloadBytes]?.quantity || 0;
  const externalUploadBytes =
    valuesByName[Resource.ManagedDataUploadBytes]?.quantity || 0;
  const internalDownloadBytes =
    valuesByName[Resource.ManagedDataInternalDownloadBytes]?.quantity || 0;
  const internalUploadBytes =
    valuesByName[Resource.ManagedDataInternalUploadBytes]?.quantity || 0;
  const storedFiles =
    valuesByName[Resource.ManagedDataStoredFiles]?.quantity || 0;
  const storedFileBytes =
    valuesByName[Resource.ManagedDataStoredBytes]?.quantity || 0;

  const formattedInvocationVCpuTime = `${perUnitMinutes(invocationVCpuMillis)} vCPU-minutes`;
  const formattedInvocationRamTime = `${perUnitMinutes(invocationRamMillis)} GiB-minutes`;
  let formattedExternalDownloadBytes = humanFileSize(externalDownloadBytes);
  let formattedInternalDownloadBytes = humanFileSize(internalDownloadBytes);
  let formattedExternalUploadBytes = humanFileSize(externalUploadBytes);
  let formattedInternalUploadBytes = humanFileSize(internalUploadBytes);
  let formattedTotalStorage = `${storedFiles} Files, ${humanFileSize(
    storedFileBytes,
  )}`;

  // The backend prices each run against its own substrate's rate sheet when the
  // run ends, so this only totals the provider-cost meters. One rate applied to
  // the cross-substrate vCPU and RAM totals would misprice MicroVM runs, which
  // AWS bills at different vCPU and memory rates, with no storage charge and
  // with a charge for each VM start.
  //
  // Runs metered under the retired generic names predate the provider-cost
  // meters, and were all Fargate, so their cost is estimated from their vCPU
  // and RAM time at Fargate's list price. No run is metered under both, so
  // nothing is counted twice.
  const retiredVCpuMillis =
    valuesByName[Resource.RetiredInvocationVCpuMillis]?.quantity || 0;
  const retiredRamMillis =
    valuesByName[Resource.RetiredInvocationRamGBMillis]?.quantity || 0;
  const retiredInvocationCost =
    (retiredVCpuMillis / 1024 / 3600) * FARGATE_VCPU_USD_PER_HOUR +
    (retiredRamMillis / 1024 / 3600) * FARGATE_RAM_GB_USD_PER_HOUR;
  const invocationProviderCost =
    sumMeteredResources(latestReport, PROVIDER_COST_MICRODOLLARS_RESOURCES) /
      1_000_000 +
    retiredInvocationCost;
  const s3DownloadCost = (0.09 * externalDownloadBytes) / (1024 * 1024 * 1024);
  const s3StorageCost = (0.023 * storedFileBytes) / (1024 * 1024 * 1024);
  const agentUnitCost = agentUnitCostUsd(agentUnitMillionths ?? 0);
  const totalCost =
    invocationProviderCost + s3DownloadCost + s3StorageCost + agentUnitCost;

  if (currentUser?.is_system_user) {
    formattedExternalDownloadBytes += " ($" + `${s3DownloadCost.toFixed(6)})`;
    formattedInternalDownloadBytes += " (free)";
    formattedExternalUploadBytes += " (free)";
    formattedInternalUploadBytes += " (free)";
    formattedTotalStorage += " ($" + `${s3StorageCost.toFixed(6)})`;
  }

  const formattedInvocationStats = (
    <div>
      Scheduled: {invocations}
      <br />
      Total Runtime: {humanDuration(invocationRuntimeMillis)}
      <br />
      vCPU Usage: {formattedInvocationVCpuTime}
      <br />
      RAM Usage: {formattedInvocationRamTime}
      <br />
      {currentUser?.is_system_user && (
        <>
          Provider Cost: ${invocationProviderCost.toFixed(6)}
          <br />
        </>
      )}
    </div>
  );

  const formattedDownloadBytes = (
    <div>
      Public Internet: {formattedExternalDownloadBytes}
      <br />
      Roboto Platform: {formattedInternalDownloadBytes}
    </div>
  );
  const formattedUploadBytes = (
    <div>
      Public Internet: {formattedExternalUploadBytes}
      <br />
      Roboto Platform: {formattedInternalUploadBytes}
    </div>
  );

  return (
    <>
      {currentUser?.is_system_user && (
        <Box
          component={Paper}
          variant="outlined"
          sx={{
            mt: theme.spacing(2),
            padding: theme.spacing(2),
            maxWidth: "400px",
            backgroundColor: theme.palette.foreground.main,
          }}
        >
          <RobotoTypography variant="subtitle2">
            Current Month-To-Date Usage
            {currentUser?.is_system_user
              ? " ($" + totalCost.toFixed(6) + ")"
              : ""}
          </RobotoTypography>
          <RobotoTypography variant="caption">
            Last updated {lastUpdated}
          </RobotoTypography>
          <List>
            <ListItem>
              <ListItemText
                primaryTypographyProps={{ variant: "body2" }}
                primary={"Datasets"}
                secondary={"Created: " + datasets}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primaryTypographyProps={{ variant: "body2" }}
                primary={"Invocations"}
                secondary={formattedInvocationStats}
                secondaryTypographyProps={{
                  component: "span",
                }}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primaryTypographyProps={{ variant: "body2" }}
                primary={"Downloads"}
                secondary={formattedDownloadBytes}
                secondaryTypographyProps={{
                  component: "span",
                }}
              />
            </ListItem>

            <ListItem>
              <ListItemText
                primaryTypographyProps={{ variant: "body2" }}
                primary={"Uploads"}
                secondary={formattedUploadBytes}
                secondaryTypographyProps={{
                  component: "span",
                }}
              />
            </ListItem>

            <ListItem>
              <ListItemText
                primaryTypographyProps={{ variant: "body2" }}
                primary={"Storage"}
                secondary={formattedTotalStorage}
                secondaryTypographyProps={{
                  component: "span",
                }}
              />
            </ListItem>
            {agentUnitMillionths !== undefined && (
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                  primary={"Agent Units"}
                  secondary={
                    <div>
                      {`Used: ${(agentUnitMillionths / 1_000_000).toFixed(2)} RAU ($${agentUnitCost.toFixed(2)})`}
                      <br />
                      {`1 RAU = $${USD_PER_AGENT_UNIT.toFixed(2)}`}
                    </div>
                  }
                  secondaryTypographyProps={{
                    component: "span",
                  }}
                />
              </ListItem>
            )}
          </List>
        </Box>
      )}
    </>
  );
};
